import { Injectable, inject } from '@angular/core';
import {
  GridColumn,
  GridState,
  GridStateChange,
  GridUtilsV2,
  StoreV2,
  unique,
  MultiselectV2ObservableItemSource,
  flatten,
  AssetService,
} from '@schenck/rotec-components';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface MachineSearchResult {
  id: string;
  name: string;
  serial: string;
  groups: string; // all groups joined by ,
  _groups: string[]; // all groups on its own
  manufacturer: string;
  plant: string;
}

export interface MachinesSearchState extends GridState<MachineSearchResult> {
  selectedIds: string[];
}

@Injectable()
export class MachinesSearchService extends StoreV2<MachinesSearchState> {
  private readonly assetService = inject(AssetService);

  private _allResults$ = new BehaviorSubject<MachineSearchResult[]>([]);
  /**
   * All results that are returned from the backend, unfiltered and not paginated.
   * Can be used for example as input for filter classes.
   */
  allResults$: Observable<MachineSearchResult[]> = this._allResults$.asObservable();
  selectedIds$: Observable<string[]> = this.state$.pipe(map((state) => state.selectedIds));
  firstSelectedResult$: Observable<MachineSearchResult | null> = this.state$.pipe(
    map((state) => {
      if (state.selectedIds.length < 1) {
        return null;
      }
      return state.results.find((res) => res.id === state.selectedIds[0]);
    })
  );

  /**
   * Grid column definitions
   */
  columns: GridColumn[] = [
    {
      key: 'name',
      notReorderable: true,
      notResizable: true,
      sortable: true,
      title: 'Machine Name',
      width: '30%',
      filter: { type: 'multiselect', items: this.getMultiselectFilterSource('name') },
    },
    {
      key: 'serial',
      notReorderable: true,
      notResizable: true,
      sortable: true,
      title: 'Machine',
      width: '20%',
      filter: { type: 'multiselect', items: this.getMultiselectFilterSource('serial') },
    },
    {
      key: '_groups',
      notReorderable: true,
      notResizable: true,
      sortable: false,
      title: 'Groups',
      width: '15%',
      filter: {
        type: 'multiselect',
        items: new MultiselectV2ObservableItemSource(
          this.allResults$.pipe(
            map((results) => unique(flatten(results.map((result) => result._groups))))
          )
        ),
      },
    },
    {
      key: 'manufacturer',
      notReorderable: true,
      notResizable: true,
      sortable: true,
      title: 'Manufacturer',
      width: '15%',
      filter: { type: 'multiselect', items: this.getMultiselectFilterSource('manufacturer') },
    },
    {
      key: 'plant',
      notReorderable: true,
      notResizable: true,
      sortable: true,
      title: 'Plant',
      width: '10%',
      filter: { type: 'multiselect', items: this.getMultiselectFilterSource('plant') },
    },
  ];

  constructor() {
    super(
      {
        filter: [],
        fulltext: '',
        page: 0,
        results: [],
        sort: [],
        total: 0,
        selectedIds: [],
        loading: true,
      },
      'MachinesSearch'
    );
    this.setAllResults();
  }

  private setAllResults() {
    combineLatest([
      this.assetService.machines$,
      this.assetService.plants$,
      this.assetService.machineGroups$,
    ])
      .pipe(
        map(([machines, plants, groups]) => {
          return machines
            .filter((machine) => !machine.isDeactivated)
            .map((machine) => {
              const plant = plants.find((_plant) => _plant.id === machine.plantId);
              const groupsOfMachine = groups
                .filter((group) => (group.childMachineIds || []).includes(machine.id))
                .map((group) => group.name);
              return <MachineSearchResult>{
                id: machine.id,
                name: machine.name,
                serial: machine.serial,
                groups: groupsOfMachine.join(', '),
                _groups: groupsOfMachine,
                manufacturer: machine.manufacturer || '',
                plant: (plant && plant.name) || '',
              };
            });
        })
      )
      .subscribe((results) => {
        this._allResults$.next(results);
        this.search({});
      });
  }

  private getMultiselectFilterSource(key: keyof MachineSearchResult) {
    return new MultiselectV2ObservableItemSource(this._allResults$, key);
  }

  /**
   * Invokes a search.
   * All parameters that are not present will be reused.
   */
  search(params: Partial<GridStateChange>) {
    const currSearchParams: GridStateChange = {
      filter: this.state.filter,
      fulltext: this.state.fulltext,
      page: this.state.page,
      sort: this.state.sort,
    };
    const newSearchParams: GridStateChange = {
      ...currSearchParams,
      ...params,
    };

    this.patchState('update search params', newSearchParams);
    this.patchState('update results', { ...this.getResults(), loading: false });
  }

  /**
   * Invokes a fulltext search.
   */
  fulltextSearch(fulltext: string) {
    this.search({ fulltext });
  }

  /**
   * Set selected ids.
   */
  setSelected(selectedIds: string[]) {
    this.patchState('set selected', { selectedIds });
  }

  /**
   * Updates the list of results after filtering and sorting.
   */
  private getResults() {
    return GridUtilsV2.getResults(this._allResults$.value, {
      filter: this.state.filter,
      fulltext: this.state.fulltext,
      page: this.state.page,
      pageSize: this.state.pageSize,
      sort: this.state.sort,
      filterFnMap: {
        name: GridUtilsV2.multiselectFilter,
        serial: GridUtilsV2.multiselectFilter,
        company: GridUtilsV2.multiselectFilter,
        _groups: (groups: string[], filterValue: string[]) => {
          if (filterValue.length === 0) {
            return true;
          }
          return groups.some((group) => filterValue.includes(group));
        },
        plant: GridUtilsV2.multiselectFilter,
      },
      resultComparator: GridUtilsV2.defaultResultComparator,
    });
  }
}
